import React from 'react';
import {
    DmsSelect,
    useGet,
    intl,
    LOCALES_FLAG,
    fetchData,
    HTTP_STATUS
} from 'dmsbasereact';
import { VirtualKeyboardEnum } from 'dmsbasereact/dist/constants/VirtualKeyboard';
import { SettingsViewEdit } from '../../components/SettingsLayouts';
import { Grid, Typography } from '@mui/material';

/**
 * GeneralSettings - Language and Keyboard configuration.
 * Manages general system settings like language and virtual keyboard preferences.
 * Uses view/edit mode pattern for consistent UX.
 */

// Convert LOCALES_FLAG object to array format for DmsSelect
const languageOptions = Object.entries(LOCALES_FLAG).map(
    ([label, value]) => ({ value, label })
);

// Convert VirtualKeyboardEnum object to array format for DmsSelect
const keyboardOptions = Object.entries(VirtualKeyboardEnum).map(
    ([label, value]) => ({ value, label })
);

/**
 * ViewContent - Display settings in read-only mode
 */
const ViewContent = ({ data }) => {
    // Find label for language
    const languageLabel = Object.entries(LOCALES_FLAG).find(
        ([_, value]) => value === data.language
    )?.[0] || data.language;

    // Find label for keyboard - VirtualKeyboardEnum is {label: value} format
    const keyboardLabel = Object.entries(VirtualKeyboardEnum).find(
        ([_, value]) => value === data.keyboard
    )?.[0] || data.keyboard;

    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                    {intl.get('SystemLanguage').d('System Language')}
                </Typography>
                <Typography variant="body1">
                    {languageLabel}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                    {intl.get('VirtualKeyboard').d('Virtual Keyboard')}
                </Typography>
                <Typography variant="body1">
                    {keyboardLabel}
                </Typography>
            </Grid>
        </Grid>
    );
};

/**
 * EditContent - Display settings as editable form fields
 */
const EditContent = ({ formData, onFieldChange }) => {
    return (
        <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
                <DmsSelect
                    name="language"
                    label={intl.get('SystemLanguage').d('System Language')}
                    fullWidth
                    value={formData.language ?? 'en_US'}
                    onChange={(value) => onFieldChange('language', value)}
                    options={languageOptions}
                    helperText={intl.get('SystemLanguageHelp').d('Default language for the system interface')}
                />
            </Grid>
            <Grid item xs={12} sm={6}>
                <DmsSelect
                    name="keyboard"
                    label={intl.get('VirtualKeyboard').d('Virtual Keyboard')}
                    fullWidth
                    value={formData.keyboard ?? 'NOT USE VIRTUAL KEYBOARD'}
                    onChange={(value) => onFieldChange('keyboard', value)}
                    options={keyboardOptions}
                    helperText={intl.get('VirtualKeyboardHelp').d('Virtual keyboard type for touchscreen devices')}
                />
            </Grid>
        </Grid>
    );
};

export const GeneralSettings = () => {
    const { data, isLoading, refetch } = useGet('/system_settings', null, true);

    const handleSave = async (payload) => {
        try {
            const response = await fetchData('/system_settings', 'PUT', true, payload, false, true);
            if (response.status === HTTP_STATUS.OK_200) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            console.error('Error saving settings:', error);
            return null;
        }
    };

    return (
        <SettingsViewEdit
            data={data?.data}
            loading={isLoading}
            title={intl.get('RegionalSettings').d('Regional Settings')}
            description={intl.get('RegionalSettingsDescription').d(
                'Configure the default language and input preferences for the system.'
            )}
            viewContent={ViewContent}
            editContent={EditContent}
            prepareFormData={(data) => ({
                language: data.language ?? 'en_US',
                keyboard: data.keyboard ?? 'NOT USE VIRTUAL KEYBOARD'
            })}
            buildPayload={(formData) => formData}
            onSave={handleSave}
            onDataUpdated={refetch}
        />
    );
};
